import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface GoalProgressBadgeProps {
  progress: number;
  className?: string;
}

const getBadgeConfig = (progress: number) => {
  if (progress >= 100) {
    return {
      label: 'Complete! 🎉',
      bgClass: 'bg-success',
      glowClass: 'shadow-[0_0_12px_hsl(var(--success)/0.5)]',
    };
  }
  if (progress >= 76) {
    return {
      label: 'Almost There!',
      bgClass: 'bg-accent',
      glowClass: 'shadow-[0_0_10px_hsl(var(--accent)/0.4)]',
    };
  }
  if (progress >= 51) {
    return {
      label: 'Making Progress',
      bgClass: 'bg-primary',
      glowClass: 'shadow-[0_0_10px_hsl(var(--primary)/0.4)]',
    };
  }
  if (progress >= 26) {
    return {
      label: 'Building',
      bgClass: 'bg-blue-500',
      glowClass: 'shadow-[0_0_10px_rgba(59,130,246,0.4)]',
    };
  }
  return {
    label: 'Just Started',
    bgClass: 'bg-slate-600',
    glowClass: 'shadow-sm',
  };
};

export const GoalProgressBadge = ({ progress, className }: GoalProgressBadgeProps) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [prevProgress, setPrevProgress] = useState(progress);

  const config = getBadgeConfig(progress);
  const prevConfig = getBadgeConfig(prevProgress);

  useEffect(() => {
    // Check if we crossed a category boundary
    if (config.label !== prevConfig.label) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 300);
      
      // Trigger confetti for 100% completion
      if (progress >= 100 && prevProgress < 100) {
        setShowConfetti(true);
        const confettiTimer = setTimeout(() => setShowConfetti(false), 2000);
        return () => {
          clearTimeout(timer);
          clearTimeout(confettiTimer);
        };
      }
      
      return () => clearTimeout(timer);
    }
    setPrevProgress(progress);
  }, [progress, config.label, prevConfig.label, prevProgress]);

  return (
    <div className={cn("relative", className)}>
      <span
        className={cn(
          "inline-flex items-center justify-center",
          "px-2.5 py-1 md:px-3 md:py-1.5",
          "text-[10px] md:text-[11px] font-semibold text-white",
          "rounded-xl",
          "transition-all duration-200",
          config.bgClass,
          config.glowClass,
          isAnimating && "animate-badge-pop"
        )}
      >
        {config.label}
      </span>
      
      {/* Confetti particles */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none overflow-visible">
          {[...Array(12)].map((_, i) => (
            <span
              key={i}
              className="absolute w-1.5 h-1.5 rounded-full animate-confetti"
              style={{
                left: '50%',
                top: '50%',
                backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#A855F7', '#3B82F6'][i % 5],
                animationDelay: `${i * 0.1}s`,
                '--confetti-x': `${(Math.random() - 0.5) * 80}px`,
                '--confetti-y': `${-Math.random() * 60 - 20}px`,
              } as React.CSSProperties}
            />
          ))}
        </div>
      )}
    </div>
  );
};
